// MT: hediye satırları + eklenti satırları + "hemen al" yönlendirmesi (spec/mt-gift-unlock.md §5.2).
// Yüklenen yerler: blocks/mt-gift-unlock, blocks/mt-addon-toggle, snippets/cart-products (MT-PATCH).
// Modül tek kez çalışır.
// Eklentiler (blocks/mt-addon-toggle): ürün formu ana ürünü ekleyince işaretli eklentiler ayrı istekle eklenir
// (biri eklenemezse hediyeler etkilenmesin) ve kutular boşaltılır. Ön-seçim yok.
//
// QA tur 2 G1 — TEK KAYNAK:
// - Hediye teklifi ürün verisidir: metafield `mt_theme.gift_offer` → snippets/mt-gift-offer JSON'u. Sayfada iki yerde
//   basılır, ikisi de aynı metafield'dan: `[data-mt-gift-offer]` (mt-gift-unlock bloğu, görüntülenen ürün) ve
//   `[data-mt-gift-offers]` (snippets/mt-cart-extras, sepetteki her ürün; listedeki ürünün teklifi yoksa hediyesi de yok).
//   Blok ayarı ya da sepet satırı yapılandırma TAŞIMAZ (eski `_mt_gift_cfg` okunmaz).
// - Satır anahtarı `_mt_gift` = "<ürün_id>:<hediye_varyant_id>" (bloktan bağımsız, kararlı). `_mt_gift_for` = hediyeyi
//   açan paket varyantları (snippets/cart-products hediye grubu ve mt-cart-extras sayacı okur).
// - Eşitleme her seferinde TAM: hedef küme = sepetteki her paket satırının teklifte açtığı hediyeler (stokta, ücretsiz
//   olamadığı bu sayfada görülmemiş). Fazlalar silinir, eksikler eklenir, aynı anahtar tek satır, adet 1.
//   Eski biçim satırlar (blok önekli anahtar / `_mt_gift_cfg`) teklifi bilinen üründe silinir ve yeni anahtarla eklenir.
//   Teklifi bu sayfada bilinmeyen ürünün hediye satırına yalnız eski kural uygulanır (açan paket sepette yoksa çıkar).
// - Doğrulama: hediye satırı sepette 0 fiyatlı değilse (indirim kurulu/aktif değil ya da eşleşmedi) satır çıkarılır,
//   anahtar bu sayfa için "verilemez" sayılır (yeniden eklenmez; döngü yok) ve console.warn yazılır.
// - `mt:gift:sync` (assets/mt-cart-events.js, sepetten paket yükseltme/düşürme) gelince eşitleme yapılır.
//
// İstek bütçesi (QA tur1 pdp-10a/10b):
// - Olayın sonucu sepet satırlarını (`detail.items`) taşıyorsa ilk okuma yapılmaz (ürün formu ve sepet bileşeni taşır).
// - Son değiştirici istek çizilen bölümleri (`sections`) birlikte ister; ayrıca `?sections=` çekilmez.
//   `cart/update.js` tüm sepeti döndürdüğü için ondan sonra `/cart.js` okunmaz; yalnız `cart/add.js` sonrası bir okuma
//   kalır (hediyenin 0,00 olduğunu doğrulamak için; add.js yanıtındaki satır fiyatına güvenilmez).
// - Yayınlanan olay `detail.items` taşır; ürün formu (product-form.js #onCartUpdate) sepeti yeniden çekmez.
// - `window.MtGift.itemsFor(variantId)`: ürün formu hediyeleri ana ürünle TEK `cart/add.js` isteğinde gönderir (sepette
//   zaten olan anahtar hariç). Ürün formunun hem tek hem kuyruk (varyant isteği yoldayken) yolu kullanır.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'mt-gift';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const KEY_RE = /^(\d+):(\d+)$/;
let queue = Promise.resolve();
// Bu modülün sepeti değiştirdiği her istek öncesi ve sonrası artar. Olay verisi beklerken değişiklik olduysa
// olayın taşıdığı satırlar bayattır; o zaman sepet yeniden okunur.
let epoch = 0;
let pending = 0;
/** Bu sayfada sepette ücretsiz olamadığı görülen hediye anahtarları (yeniden eklenmez). @type {Set<string>} */
const failedKeys = new Set();

const isGift = (item) => Boolean(item.properties && item.properties._mt_gift);

async function getCart() {
  const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error('cart.js ' + response.status);
  return response.json();
}

async function post(path, body) {
  epoch++;
  try {
    const response = await fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!response.ok && response.status !== 422) throw new Error(path + ' ' + response.status);
    return response.json();
  } finally {
    epoch++;
  }
}

/** @param {any} offer @returns {boolean} */
function validOffer(offer) {
  if (!offer || !offer.product || !Array.isArray(offer.gifts)) return false;
  const now = Date.now() / 1000;
  // Önbellekteki sayfa: kampanya aralığı dışındaysa teklif yok sayılır (uyum U3; Liquid de aynı aralığı uygular).
  if (offer.start && now < Number(offer.start)) return false;
  if (offer.end && now > Number(offer.end)) return false;
  return true;
}

/**
 * Sayfadaki teklifler. `known` = hediye durumu bilinen ürünler (teklifi olan + sepet ekinde listelenen teklifsiz ürünler).
 * @returns {{ offers: Map<string, any>, known: Set<string> }}
 */
function readOffers() {
  const offers = new Map();
  const known = new Set();
  /** @param {any} offer */
  const take = (offer) => {
    if (!validOffer(offer)) return;
    const id = String(offer.product);
    known.add(id);
    if (!offers.has(id)) offers.set(id, offer);
  };
  document.querySelectorAll('[data-mt-gift-offer]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    try {
      take(JSON.parse(el.dataset.mtGiftOffer || ''));
    } catch {
      /* teklif yok (boş öznitelik) */
    }
  });
  document.querySelectorAll('script[data-mt-gift-offers]').forEach((el) => {
    try {
      const data = JSON.parse(el.textContent || '{}');
      (data.offers || []).forEach(take);
      (data.products || []).forEach((id) => known.add(String(id)));
    } catch {
      /* bozuk veri: bu kaynak yok sayılır */
    }
  });
  return { offers, known };
}

/** @param {any} gift */
function giftLine(gift) {
  return {
    id: Number(gift.variant),
    quantity: 1,
    properties: { _mt_gift: String(gift.key), _mt_gift_for: gift.for.map(String).join(',') },
  };
}

/**
 * Hedef küme: paket satırlarının açtığı hediyeler (anahtar → hediye).
 * @param {any[]} mains @param {Map<string, any>} offers
 */
function targetGifts(mains, offers) {
  const target = new Map();
  for (const main of mains) {
    const offer = offers.get(String(main.product_id));
    if (!offer) continue;
    for (const gift of offer.gifts) {
      if (!gift || gift.available === false || !Array.isArray(gift.for) || failedKeys.has(String(gift.key))) continue;
      if (gift.for.some((id) => String(id) === String(main.variant_id))) target.set(String(gift.key), gift);
    }
  }
  return target;
}

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

/** İsteğe çizilecek sepet bölümlerini ekler (Shopify bundled section rendering). */
function withSections(body, wanted) {
  const ids = sectionIds();
  if (wanted && ids.length) {
    body.sections = ids.join(',');
    body.sections_url = window.location.pathname;
  }
  return body;
}

/** Yedek: değiştirici istek bölüm döndürmediyse (ör. son istek 422) bölümleri ayrıca çeker. */
async function fetchSections() {
  const ids = sectionIds();
  if (!ids.length) return {};
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    return await fetch(url.toString(), { credentials: 'same-origin' }).then((r) => r.json());
  } catch (error) {
    return {};
  }
}

/** Sepeti çizen bölümleri (çekmece, sepet sayfası) Horizon'un kendi olayıyla tazeler. */
async function publish(cart, sections) {
  const rendered = sections && Object.keys(sections).length ? sections : await fetchSections();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: SOURCE, lines: [], promise: deferred.promise })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source: SOURCE, sections: rendered, itemCount: cart.item_count, items: cart.items },
  });
}

/**
 * Tam eşitleme (hedef küme). `withAddons`: ürün formu ekledikten sonra işaretli eklentiler de eklenir.
 * @param {boolean} withAddons
 * @param {{ items: any[] } | null} [known]  olayın taşıdığı güncel sepet satırları (yoksa /cart.js okunur)
 */
async function sync(withAddons, known) {
  let cart = known || (await getCart());
  const mains = cart.items.filter((i) => !isGift(i));
  const packs = new Set(mains.map((i) => String(i.variant_id)));
  const { offers, known: knownProducts } = readOffers();
  const target = targetGifts(mains, offers);
  const updates = {};
  const kept = new Set();

  for (const line of cart.items.filter(isGift)) {
    const key = String(line.properties._mt_gift);
    const unlockers = String(line.properties._mt_gift_for || '').split(',').filter(Boolean);
    const match = KEY_RE.exec(key);
    // Satırın ana ürünü: yeni anahtarda önek; eski biçimde açan paketin ürünü.
    const owner = match
      ? match[1]
      : String((mains.find((m) => unlockers.includes(String(m.variant_id))) || {}).product_id || '');
    if (owner && knownProducts.has(owner)) {
      // Teklif biliniyor: yalnız hedef kümedeki yeni biçim anahtar kalır (eski biçim silinir, yenisi eklenir).
      if (match && target.has(key) && !kept.has(key) && String(line.variant_id) === match[2]) {
        kept.add(key);
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        updates[line.key] = 0;
      }
      continue;
    }
    // Teklif bu sayfada bilinmiyor: eski kural (açan paket sepette değilse ya da yinelenen anahtarsa çıkar).
    const qualifies = unlockers.some((id) => packs.has(id));
    if (!qualifies || kept.has(key)) {
      updates[line.key] = 0;
    } else {
      kept.add(key);
      if (line.quantity !== 1) updates[line.key] = 1;
    }
  }

  const adds = [];
  for (const [key, gift] of target) {
    if (!kept.has(key)) adds.push(giftLine(gift));
  }

  const addons = [];
  if (withAddons) {
    document.querySelectorAll('mt-addon-toggle [data-mt-addon]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement && input.value) addons.push(input);
    });
  }

  // Bölümleri yalnız son değiştirici istek ister; temizlik (update.js) sona alınır çünkü tüm sepeti döndürür.
  const hasUpdates = Object.keys(updates).length > 0;
  const last = hasUpdates ? 'updates' : adds.length ? 'gifts' : addons.length ? 'addons' : null;
  let sections = null;
  let fresh = null;
  let changed = false;

  if (addons.length) {
    const result = await post('cart/add.js', withSections({ items: addons.map((input) => ({ id: Number(input.value), quantity: 1 })) }, last === 'addons'));
    if (result && result.status) {
      console.error('[mt-addon] eklenti eklenemedi:', result.description || result.message);
    } else {
      addons.forEach((input) => (input.checked = false));
      changed = true;
      if (result && result.sections) sections = result.sections;
    }
  }
  if (adds.length) {
    const result = await post('cart/add.js', withSections({ items: adds }, last === 'gifts'));
    if (result && result.status) {
      // Eklenemeyen hediye (stok vb.): bu sayfada yeniden denenmez.
      adds.forEach((line) => failedKeys.add(line.properties._mt_gift));
      console.warn('[mt-gift] hediye eklenemedi:', result.description || result.message);
    } else {
      changed = true;
      if (result && result.sections) sections = result.sections;
    }
  }
  if (hasUpdates) {
    const result = await post('cart/update.js', withSections({ updates }, true));
    changed = true;
    if (result && Array.isArray(result.items)) fresh = result;
    sections = (result && result.sections) || null;
  }
  if (changed) cart = fresh || (await getCart());

  // Doğrulama her eşitlemede: müşteri bir hediyeyi silince paket indirimi (gets = tüm hediyeler) düşebilir ve kalanlar ücretli olur.
  if (!changed && !cart.items.some(isGift)) return cart;
  const notFree = cart.items.filter((i) => isGift(i) && i.final_line_price > 0);
  const failed = notFree.map((i) => String(i.properties._mt_gift));
  const ok = cart.items.filter((i) => isGift(i) && i.final_line_price === 0).map((i) => String(i.properties._mt_gift));
  document.dispatchEvent(new CustomEvent('mt:gift-status', { detail: { failed, ok } }));
  if (notFree.length) {
    failed.forEach((key) => failedKeys.add(key));
    console.warn(
      '[mt-gift] Hediye satırı ücretsiz değil; sepetten çıkarıldı. Shopify Buy X Get Y indirimi kurulu ve aktif mi?',
      notFree.map((i) => ({ variant: i.variant_id, tier: i.properties._mt_gift, final_line_price: i.final_line_price }))
    );
    const remove = {};
    notFree.forEach((i) => {
      remove[i.key] = 0;
    });
    const result = await post('cart/update.js', withSections({ updates: remove }, true));
    cart = result && Array.isArray(result.items) ? result : await getCart();
    sections = (result && result.sections) || null;
    changed = true;
  }
  if (changed) await publish(cart, sections);
  return cart;
}

function enqueue(task) {
  pending++;
  queue = queue
    .then(task)
    .catch((error) => console.error('[mt-gift]', error))
    .finally(() => {
      pending--;
    });
  return queue;
}

/**
 * Ürün formu için: bu varyant eklenince açılacak ve sepette henüz olmayan hediye satırları.
 * Sepetteki anahtarlar çizili sepetten okunur (gizli hediye satırlarındaki data-mt-gift-tier = `_mt_gift`).
 * @param {string | number | null | undefined} variantId
 * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
 */
function itemsFor(variantId) {
  const id = String(variantId || '');
  if (!id) return [];
  const inCart = new Set(
    Array.from(document.querySelectorAll('cart-items-component [data-mt-gift-tier]')).map((row) =>
      row instanceof HTMLElement ? row.dataset.mtGiftTier : ''
    )
  );
  const items = [];
  const seen = new Set();
  for (const offer of readOffers().offers.values()) {
    for (const gift of offer.gifts) {
      const key = String(gift && gift.key);
      if (!gift || gift.available === false || !Array.isArray(gift.for)) continue;
      if (inCart.has(key) || seen.has(key) || failedKeys.has(key)) continue;
      if (!gift.for.some((unlocker) => String(unlocker) === id)) continue;
      seen.add(key);
      items.push(giftLine(gift));
    }
  }
  return items;
}

window.MtGift = Object.assign(window.MtGift || {}, { itemsFor });

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.context === SOURCE || !event.promise) return;
  const fromProductForm = event.action === 'add' && event.context === 'product';
  event.promise
    .then((result) => {
      const detail = (result && result.detail) || {};
      if (detail.source === SOURCE) return;
      const failed = Boolean(detail.didError);
      // Olay verisi yalnız şu an kuyruk boşsa güvenilir; kuyrukta bekleyen/çalışan eşitleme varsa, ya da veri
      // geldikten sonra bu modül sepeti değiştirdiyse, eşitleme sepeti kendisi okur.
      const seen = epoch;
      const items = pending === 0 && Array.isArray(detail.items) ? detail.items : null;
      enqueue(async () => {
        const known = items && epoch === seen ? { items } : null;
        await sync(fromProductForm && !failed, known);
      });
    })
    .catch(() => {});
});

// Sepetten paket yükseltildi/düşürüldü (mt-cart-events, yedek yol dahil): küme hedef pakete göre yeniden kurulur.
document.addEventListener('mt:gift:sync', () => enqueue(() => sync(false)));

// Açılışta bir kez: sepette hediye satırı ya da teklifli ürün varsa eşitle (paketi başka sekmede değişmiş, eski biçim
// satır kalmış ya da hediye eksik olabilir).
{
  const offersEl = document.querySelector('script[data-mt-gift-offers]');
  let hasOffer = false;
  try {
    hasOffer = Boolean(offersEl && (JSON.parse(offersEl.textContent || '{}').offers || []).length);
  } catch {
    hasOffer = false;
  }
  if (document.querySelector('[data-mt-gift-line]') || hasOffer) enqueue(() => sync(false));
}
